/**
 * @fileoverview Table header configuration for "Query" and "Auth Required" auth status tabs.
 *
 * This module defines the column structure for displaying medical PA orders
 * that are in query or auth required states. These states require additional
 * action and include a side modal icon for quick note logging and follow-up.
 *
 * @module queryAuthRequiredTableHeaders
 */

import { TableCellType } from "../../../../components/custom-table/table";
import { GenericAuthStatusTableColumnKeys } from "../../../../enums/tableColumnKeys";
import { MedicalPaOrdersAlgoliaSortFields } from "../../medicalPaOrdersAlgolia/utils/medicalPaOrdersTable";

/**
 * Returns the table header configuration for "Query" and "Auth Required" auth status tabs.
 *
 * @description
 * This configuration is designed for orders requiring follow-up action.
 * Key features:
 * - Standard patient details column (no left border indicator)
 * - "Auth Status" column shows the current authorization verification status
 * - Includes side modal icon for logging notes and follow-up actions
 * - Total column widths sum to 100%
 *
 * @returns {Array<Object>} Array of table header configuration objects
 *
 * @example
 * const headers = getQueryAuthRequiredTableHeaders();
 * // Use headers to render the table for query or auth_required tabs
 */
export const getQueryAuthRequiredTableHeaders = () => {
  return [
    {
      label: "Patient Name",
      key: GenericAuthStatusTableColumnKeys.PATIENT_DETAILS,
      order: 1,
      width: 12,
      sortable: true,
      type: TableCellType.MULTILINE,
      subKey: "mainText",
      sortField: MedicalPaOrdersAlgoliaSortFields.FIRST_NAME,
    },
    {
      label: "Date of Work",
      key: GenericAuthStatusTableColumnKeys.DATE_OF_WORK,
      order: 2,
      width: 8,
      sortable: true,
      type: TableCellType.DATE,
      sortField: MedicalPaOrdersAlgoliaSortFields.DATE_OF_WORK,
    },
    {
      label: "Plan Details",
      key: GenericAuthStatusTableColumnKeys.PLAN_DETAILS,
      order: 3,
      width: 13,
      sortable: true,
      type: TableCellType.MULTILINE,
      subKey: "mainText",
      sortField: MedicalPaOrdersAlgoliaSortFields.PRIMARY_PAYER_NAME,
    },
    {
      label: "Medication",
      key: GenericAuthStatusTableColumnKeys.MEDICATION,
      order: 4,
      width: 10,
      sortable: true,
      type: TableCellType.STRING,
      sortField: MedicalPaOrdersAlgoliaSortFields.REGIMEN_NAME,
    },
    {
      label: "Provider",
      key: GenericAuthStatusTableColumnKeys.PROVIDER,
      order: 5,
      width: 12,
      sortable: true,
      type: TableCellType.STRING,
      sortField: MedicalPaOrdersAlgoliaSortFields.PRACTITIONER_NAME,
    },
    {
      label: "DOS",
      key: GenericAuthStatusTableColumnKeys.DATE_OF_SERVICE,
      order: 6,
      width: 8,
      sortable: true,
      type: TableCellType.DATE,
      sortField: MedicalPaOrdersAlgoliaSortFields.DATE_OF_SERVICE,
    },
    {
      label: "BO Value",
      key: GenericAuthStatusTableColumnKeys.BO_VALUE,
      order: 7,
      width: 10,
      type: TableCellType.BADGE,
    },
    {
      label: "Auth Status",
      key: GenericAuthStatusTableColumnKeys.AUTH_VERIFICATION,
      order: 8,
      width: 10,
      type: TableCellType.BADGE,
    },
    {
      label: "",
      key: GenericAuthStatusTableColumnKeys.RPA_STATUS,
      order: 9,
      width: 4,
      type: TableCellType.RPA_STATUS,
    },
    {
      label: "",
      key: GenericAuthStatusTableColumnKeys.RPA_STATUS_TWO,
      order: 10,
      width: 3,
      type: TableCellType.RPA_STATUS_TWO,
    },
    {
      label: "",
      key: GenericAuthStatusTableColumnKeys.ORDER_PROCESS_AND_REVIEW_STATUS,
      order: 11,
      width: 7,
      type: TableCellType.OVERLAPPING_ICONS_WITH_POPOVER_AND_ACTIONS,
    },
    {
      label: "",
      key: GenericAuthStatusTableColumnKeys.SIDE_MODAL_ICON,
      order: 12,
      width: 3,
      type: TableCellType.SIDE_MODAL_ICON,
    },
  ];
};
